import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails, Grid, List, ListItem, Typography, Card } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FileStatusIcon } from '../../../FileStatusIcon';

const useStyles = makeStyles((theme) => ({
  error: { backgroundColor: `${theme.palette.error.main}0D` },
  valid: { backgroundColor: `${theme.palette.success.main}0D` },
  modified: { backgroundColor: `${theme.palette.tertiary.main}0D` },
  draft: { backgroundColor: `${theme.palette.info.main}0D` },
}));

export const TableErrorDetails = ({ validation }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const getClassName = (status) => classes?.[status] ?? classes?.draft;

  const getStatusMessage = (validation) => {
    const messages = {
      error: (
        <Typography color="error.main">
          {validation.details?.length}{' '}
          {t('commoncomponents.datastore.tableValidation.validationErrors', 'validation error(s)')}
        </Typography>
      ),
      valid: (
        <Typography color="success.main">
          {t('commoncomponents.datastore.tableValidation.valid', 'Valid table')}
        </Typography>
      ),
      modified: (
        <Typography color="tertiary.main">
          {t(
            'commoncomponents.datastore.tableValidation.tableModified',
            'This table has been modified and must be validated'
          )}
        </Typography>
      ),
      draft: (
        <Typography>
          {t('commoncomponents.datastore.tableValidation.tableNotValidated', 'This table has not yet been validated')}
        </Typography>
      ),
    };

    return (
      <Grid container style={{ gap: '16px', alignItems: 'center' }}>
        <FileStatusIcon status={validation.status} />
        <span style={{ position: 'relative', top: '2px' }}>{messages[validation.status || 'draft']}</span>
      </Grid>
    );
  };

  return (
    <>
      {validation.details?.length > 0 ? (
        <Accordion>
          <AccordionSummary className={getClassName(validation.status)} expandIcon={<ExpandMore />}>
            {getStatusMessage(validation)}
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{ listStyleType: 'disc', listStylePosition: 'inside', maxHeight: '150px', overflow: 'auto' }}>
              {validation.details.map((detail) => (
                <ListItem key={detail} sx={{ display: 'list-item' }}>
                  {typeof detail === 'string' ? detail : JSON.stringify(detail)}
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Card className={getClassName(validation.status)} sx={{ display: 'flex', p: 1.5, overflow: 'unset' }}>
          {getStatusMessage(validation)}
        </Card>
      )}
    </>
  );
};

TableErrorDetails.propTypes = {
  validation: PropTypes.object.isRequired,
};
